import React from "react";
import { ImageOff } from "lucide-react";
import { getSupportTicket } from "../../apis/supportService";
import { useSignedUrlRetry, shareInflight } from "../../hooks/useSignedUrlRetry";

const FALLBACK_TEXT = "Could not load attachment";

const getAttachmentSrc = (attachment) => attachment?.url || attachment?.path;

/**
 * Ticket attachment image. Attachment URLs are short-lived pre-signed S3 URLs, so on a load error
 * we refetch /support/tickets/:id once, swap in the fresh URL and retry once. If that fails too we
 * show "Could not load attachment" (icon only + tooltip when `compact`, e.g. thumbnails).
 */
export function TicketAttachmentImage({
  ticketId,
  attachment,
  alt,
  className = "",
  fallbackClassName = "",
  compact = false,
  ...imgProps
}) {
  const refetchUrl = async () => {
    const ticket = await shareInflight(`support-ticket:${ticketId}`, () => getSupportTicket(ticketId));
    const fresh = ticket?.attachments?.find(
      (a) =>
        (attachment?.attachmentId != null && a.attachmentId === attachment.attachmentId) ||
        (attachment?.attachmentId == null && a.filename === attachment?.filename)
    );
    return getAttachmentSrc(fresh) || null;
  };

  const { src, failed, refreshing, onError } = useSignedUrlRetry(getAttachmentSrc(attachment), refetchUrl);

  if (failed || !src) {
    return (
      <div
        role="img"
        aria-label={FALLBACK_TEXT}
        title={FALLBACK_TEXT}
        className={`flex h-full w-full flex-col items-center justify-center gap-1 text-center text-gray-500 ${fallbackClassName}`}
      >
        <ImageOff className={compact ? "h-4 w-4" : "h-6 w-6"} />
        {!compact && <span className="text-sm">{FALLBACK_TEXT}</span>}
      </div>
    );
  }

  return (
    <img
      {...imgProps}
      src={src}
      alt={alt}
      className={`${className}${refreshing ? " invisible" : ""}`}
      onError={onError}
    />
  );
}

export default TicketAttachmentImage;
